import {
  EAutoModeCapability,
  EAutoModeConfirmationPolicy,
  type IOrganization,
  type TAutoModeConfirmationPolicies,
} from "../types/organizations";

// NOTE: NSAI-6543 — the capabilities in the order both settings screens list
// them.
export const AUTO_MODE_CAPABILITIES: readonly EAutoModeCapability[] = [
  EAutoModeCapability.WEB_SEARCH,
  EAutoModeCapability.DEEP_RESEARCH,
  EAutoModeCapability.IMAGE_GENERATION,
  EAutoModeCapability.DOCUMENT_GENERATION,
  EAutoModeCapability.DECK,
  EAutoModeCapability.HTML_PAGE,
] as const;

export const AUTO_MODE_CONFIRMATION_POLICIES: readonly EAutoModeConfirmationPolicy[] =
  [
    EAutoModeConfirmationPolicy.RUN,
    EAutoModeConfirmationPolicy.CONFIRM_LOW_CONFIDENCE,
    EAutoModeConfirmationPolicy.ALWAYS_CONFIRM,
  ] as const;

// NOTE: Mirrors the server's DEFAULT_AUTO_MODE_CONFIRMATION_POLICIES — what an
// org reads as before an owner changes anything.
export const DEFAULT_AUTO_MODE_CONFIRMATION_POLICIES: TAutoModeConfirmationPolicies =
  {
    [EAutoModeCapability.WEB_SEARCH]: EAutoModeConfirmationPolicy.RUN,
    [EAutoModeCapability.DEEP_RESEARCH]:
      EAutoModeConfirmationPolicy.ALWAYS_CONFIRM,
    [EAutoModeCapability.IMAGE_GENERATION]:
      EAutoModeConfirmationPolicy.CONFIRM_LOW_CONFIDENCE,
    [EAutoModeCapability.DOCUMENT_GENERATION]: EAutoModeConfirmationPolicy.RUN,
    [EAutoModeCapability.DECK]:
      EAutoModeConfirmationPolicy.CONFIRM_LOW_CONFIDENCE,
    [EAutoModeCapability.HTML_PAGE]:
      EAutoModeConfirmationPolicy.CONFIRM_LOW_CONFIDENCE,
  };

// NOTE: The i18n key suffix per capability, under
// settings:chat_settings.auto_mode.capabilities.<key>.
export const AUTO_MODE_CAPABILITY_LABEL_KEYS: Record<
  EAutoModeCapability,
  string
> = {
  [EAutoModeCapability.WEB_SEARCH]: "web_search",
  [EAutoModeCapability.DEEP_RESEARCH]: "deep_research",
  [EAutoModeCapability.IMAGE_GENERATION]: "image_generation",
  [EAutoModeCapability.DOCUMENT_GENERATION]: "document_generation",
  [EAutoModeCapability.DECK]: "deck",
  [EAutoModeCapability.HTML_PAGE]: "html_page",
};

export interface IAutoModeSettingsView {
  isAutoModeEnabled: boolean;
  isAutoModeDefault: boolean;
  autoModeCapabilities: EAutoModeCapability[];
  autoModeConfirmationPolicies: TAutoModeConfirmationPolicies;
}

export interface IResolveAutoModeSettingsViewProps {
  organization:
    | Pick<
        IOrganization,
        | "isAutoModeEnabled"
        | "isAutoModeDefault"
        | "autoModeCapabilities"
        | "autoModeConfirmationPolicies"
      >
    | null
    | undefined;
}

// NOTE: NSAI-6543 — the controls as both settings screens show them, with the
// deployment defaults filled in for anything a server did not send.
export const resolveAutoModeSettingsView = ({
  organization,
}: IResolveAutoModeSettingsViewProps): IAutoModeSettingsView => ({
  isAutoModeEnabled: organization?.isAutoModeEnabled ?? true,
  isAutoModeDefault: organization?.isAutoModeDefault ?? false,
  autoModeCapabilities: organization?.autoModeCapabilities ?? [
    ...AUTO_MODE_CAPABILITIES,
  ],
  autoModeConfirmationPolicies: {
    ...DEFAULT_AUTO_MODE_CONFIRMATION_POLICIES,
    ...(organization?.autoModeConfirmationPolicies ?? {}),
  },
});

export interface IToggleAutoModeCapabilityProps {
  capabilities: readonly EAutoModeCapability[];
  capability: EAutoModeCapability;
  isAllowed: boolean;
}

// NOTE: The next full allow-list after one switch flips, in canonical order.
export const toggleAutoModeCapability = ({
  capabilities,
  capability,
  isAllowed,
}: IToggleAutoModeCapabilityProps): EAutoModeCapability[] =>
  AUTO_MODE_CAPABILITIES.filter((candidate) =>
    candidate === capability ? isAllowed : capabilities.includes(candidate),
  );
